/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import {
    Select,
    Icon,
    Button,
    StyledModal,
    ModalActions,
    ButtonGroup,
} from 'sarsaparilla';
import { connect } from 'react-redux';
import TripDestinationsForm from './TripDestinationsForm';
import * as tripPlannerActions from '../actions/tripPlanner';
import { metersToMiles, isTripEditable, isTripValid } from '../utils/tripUtils';

const bufferOptions = [
    {
        label: 'Buffer - 5 miles',
        value: '5',
    },
    {
        label: 'Buffer - 10 miles',
        value: '10',
    },
    {
        label: 'Buffer - 15 miles',
        value: '15',
    },
    {
        label: 'Buffer - 25 miles',
        value: '25',
    },
    {
        label: 'Buffer - 50 miles',
        value: '50',
    },
    {
        label: 'Buffer - 100 miles',
        value: '100',
    },
    {
        label: 'Buffer - unlimited',
        value: '-1',
    },
];
const radiusOptions = [
    {
        label: 'Radius - 5 miles',
        value: '5',
    },
    {
        label: 'Radius - 10 miles',
        value: '10',
    },
    {
        label: 'Radius - 15 miles',
        value: '15',
    },
    {
        label: 'Radius - 25 miles',
        value: '25',
    },
    {
        label: 'Radius - 50 miles',
        value: '50',
    },
    {
        label: 'Radius - 100 miles',
        value: '100',
    },
    {
        label: 'Radius - unlimited',
        value: '-1',
    },
];

const propTypes = {
    tripPlanner: PropTypes.object,
    dispatch: PropTypes.func,
    showEditRouteModal: PropTypes.bool,
};

const defaultProps = {};

export class TripSummary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            destinationsModalOpen: false,
            showRouteEditErrors: false,
            activeDestinationIndex: null,
            trip: JSON.parse(JSON.stringify(props.tripPlanner.trip)),
        };
        this.editRouteClicked = this.editRouteClicked.bind(this);
        this.cancelEditRouteModal = this.cancelEditRouteModal.bind(this);
        this.applyEditRouteModal = this.applyEditRouteModal.bind(this);
        this.handleRadiusChange = this.handleRadiusChange.bind(this);
        this.addDestination = this.addDestination.bind(this);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.showEditRouteModal !== this.state.destinationsModalOpen) {
            const editable = isTripEditable(this.props.tripPlanner.trip);

            if (editable) {
                const nextState = {};
                if (nextProps.showEditRouteModal) {
                    // clone trip for editing
                    nextState.trip = JSON.parse(
                        JSON.stringify(this.props.tripPlanner.trip)
                    );
                }
                nextState.destinationsModalOpen = nextProps.showEditRouteModal;
                this.setState(nextState);
            }
        }
    }

    getTripDuration() {
        let duration = 0;

        this.props.tripPlanner.trip.destinations.forEach((destination) => {
            if (destination.duration) {
                duration += destination.duration;
            }
        });

        if (duration > 1) {
            return `${duration} Days`;
        }

        return `${duration} Day`;
    }

    getTripDestinations() {
        let counter = 0;

        this.props.tripPlanner.trip.destinations.forEach((destination) => {
            if (destination.name) {
                counter += 1;
            }
        });

        if (counter > 1) {
            return `${counter} Destinations`;
        }

        return `${counter} Destination`;
    }

    getTripLength() {
        if (
            this.props.tripPlanner.directions &&
            this.props.tripPlanner.directions.routes &&
            this.props.tripPlanner.directions.routes.length > 0
        ) {
            const distance = this.props.tripPlanner.directions.routes[0].distance;

            if (distance) {
                // meters to miles
                return `${metersToMiles(distance)} Total Miles`;
            }
        }

        return null;
    }

    addDestination() {
        const destination = {
            name: '',
            lat: null,
            lng: null,
            entity_id: null,
            entity_type: null,
            items: [],
        };

        const trip = this.state.trip;

        if (trip.destinations.length > 0) {
            destination.duration = 1;
        }

        trip.destinations[trip.destinations.length] = destination;

        this.setState({
            trip,
            activeDestinationIndex: trip.destinations.length - 1,
        });
    }

    editRouteClicked() {
        this.props.dispatch(tripPlannerActions.setShowEditRouteModal(true));
    }

    handleRadiusChange(evt) {
        const value = evt.target.value;
        this.props.dispatch(tripPlannerActions.setBufferRadius(value));
    }

    applyEditRouteModal() {
        if (!isTripValid(this.state.trip)) {
            this.setState({
                showRouteEditErrors: true,
            });
        } else {
            this.props.dispatch(tripPlannerActions.setShowEditRouteModal(false));
            this.props.dispatch(tripPlannerActions.setTrip(this.state.trip, true));
            this.props.dispatch(
                tripPlannerActions.fetchDrivingDirections(this.state.trip)
            );
            this.props.dispatch(tripPlannerActions.fetchDetailedRoute(this.state.trip));
        }
    }

    cancelEditRouteModal() {
        this.props.dispatch(tripPlannerActions.setShowEditRouteModal(false));
    }

    render() {
        const currentRadius = `${this.props.tripPlanner.trip.radius}`;
        const duration = this.getTripDuration();
        const destinations = this.getTripDestinations();
        const tripLength = this.getTripLength();
        const editable = isTripEditable(this.props.tripPlanner.trip);

        return (
            <div className="trip-planner-summary" data-testid="trip-planner-summary">
                <ul className="trip-planner-route-summary">
                    <li>
                        <Icon iconName="calendar-note" />
                        {duration}
                    </li>
                    <li>
                        <Icon iconName="pin" />
                        {destinations}
                    </li>
                    <li>
                        <Icon iconName="map" />
                        {tripLength}
                    </li>
                </ul>

                <div className="trip-planner-route-actions">
                    {editable && (
                        <Button
                            appearance="link"
                            iconBeforeElement={<Icon iconName="edit" />}
                            title="Edit Route"
                            aria-label="Edit Route"
                            className="mr-1"
                            onClick={this.editRouteClicked}
                        >
                            Edit Route
                        </Button>
                    )}

                    <Select
                        id="radius-dropdown"
                        label="Radius"
                        isLabelVisible={false}
                        options={
                            this.props.tripPlanner.view === 'list'
                                ? radiusOptions
                                : bufferOptions
                        }
                        onChange={this.handleRadiusChange}
                        value={currentRadius}
                    />
                </div>

                <StyledModal
                    isOpen={this.state.destinationsModalOpen}
                    shouldActionsStickInMobile
                    role="complementary"
                    size={'lg'}
                    onAfterOpen={() => {}}
                    onRequestClose={this.cancelEditRouteModal}
                    ariaHideApp={false}
                    contentLabel="Edit Trip Route Modal"
                >
                    <TripDestinationsForm
                        trip={this.state.trip}
                        activeDestinationIndex={this.state.activeDestinationIndex}
                        showValidationErrors={this.state.showRouteEditErrors}
                        showAddDestinationButton={false}
                        headingLevel={1}
                    />
                    <ModalActions>
                        <ButtonGroup>
                            <Button
                                appearance="link"
                                onClick={this.addDestination}
                                title="Add Destination"
                                iconBeforeElement={<Icon iconName="add-circle-outline" />}
                                aria-label="Add Destination"
                            >
                                Add Destination
                            </Button>
                            <Button
                                appearance="tertiary"
                                title="Cancel"
                                onClick={this.cancelEditRouteModal}
                                aria-label="Cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                title="Apply"
                                onClick={this.applyEditRouteModal}
                                aria-label="Apply"
                            >
                                Apply
                            </Button>
                        </ButtonGroup>
                    </ModalActions>
                </StyledModal>
            </div>
        );
    }
}

TripSummary.propTypes = propTypes;
TripSummary.defaultProps = defaultProps;

function select(state) {
    return {
        tripPlanner: state.tripPlanner,
        showEditRouteModal: state.tripPlanner.showEditRouteModal,
    };
}

export default connect(select)(TripSummary);
